import { isDefined } from '@sgme/fp';
import { INITIAL_QUOTE_LABEL, SECOND_QUOTE_LABEL } from 'components/common-form/sidePanel/backTest/shared';
import type { Point as HighchartsPoint, Options, Series, SeriesLineOptions } from 'highcharts/highstock';
import { useSgBsTheme } from 'hooks/useSgBsTheme';
import type { IntlKey } from 'locales';
import { useIntl } from 'react-intl';
import type { Point, QuoteIds, QuoteLabels, Serie } from 'state/backTest/backTest.model';
import { exportMenuItems } from 'utils/highcharts';

export type SeriesOptions<T> = {
  quoteIds: QuoteIds;
  quoteLabels: QuoteLabels;
  series: readonly Serie<T>[];
  hasErrorForInitialQuoteId: boolean;
  hasErrorForSecondQuoteId: boolean;
  precision: number;
  backTestId: string;
  titleKey: IntlKey;
  xAxisKey: IntlKey;
  yAxisKey: IntlKey;
  /** @default false */
  showMarkers?: boolean;
};
type QuoteSeriesLineOptions = SeriesLineOptions & { id: string; showId: boolean };

const PERCENT_FORMAT = '{value}%';

function labelFormatter(this: Series | HighchartsPoint) {
  const { name, options } = this;
  const { id, showId } = options as QuoteSeriesLineOptions;
  return showId ? `${name} ${id}` : name;
}
export function useBackTestOptions<T>({
  quoteIds,
  quoteLabels,
  series,
  titleKey,
  precision,
  backTestId,
  xAxisKey,
  yAxisKey,
  hasErrorForInitialQuoteId,
  hasErrorForSecondQuoteId,
  showMarkers = false,
}: SeriesOptions<T>): Options {
  const sgbsTheme = useSgBsTheme();
  const initialQuoteSerie = series.find(serie => serie.quoteId === quoteIds.initialQuoteId);
  const secondQuoteSerie = series.find(serie => serie.quoteId === quoteIds.secondQuoteId);
  const { formatMessage } = useIntl();
  const title = formatMessage({ id: titleKey });
  const xAxisTitle = formatMessage({ id: xAxisKey });
  const yAxisTitle = formatMessage({ id: yAxisKey });
  const initialQuoteSerieOption: QuoteSeriesLineOptions | undefined =
    !!initialQuoteSerie && !hasErrorForInitialQuoteId
      ? {
          name: quoteLabels.initialQuoteLabel ?? INITIAL_QUOTE_LABEL,
          type: 'line',
          colorIndex: 0,
          marker: { enabled: showMarkers },
          id: initialQuoteSerie.quoteId,
          showId: !quoteLabels.initialQuoteLabel,
          data: initialQuoteSerie.data as Point<T>[],
        }
      : undefined;
  const secondQuoteSerieOption: QuoteSeriesLineOptions | undefined =
    !!secondQuoteSerie && !hasErrorForSecondQuoteId
      ? {
          id: secondQuoteSerie.quoteId,
          name: quoteLabels.secondQuoteLabel ?? SECOND_QUOTE_LABEL,
          type: 'line',
          lineWidth: 1,
          colorIndex: 1,
          marker: { enabled: showMarkers },
          data: secondQuoteSerie.data as Point<T>[],
          showId: !quoteLabels.secondQuoteLabel,
          dashStyle: 'ShortDash',
        }
      : undefined;
  const optionSeries = [initialQuoteSerieOption, secondQuoteSerieOption].filter(isDefined);

  const computedStyle = getComputedStyle(document.querySelector('[class*="highcharts"]') ?? document.documentElement);
  const backgroundColor = computedStyle.getPropertyValue('--highcharts-background-color');
  const primaryColor = computedStyle.getPropertyValue('--bs-primary');
  const secondaryColor = computedStyle.getPropertyValue('--bs-secondary');

  return {
    credits: { enabled: false },
    accessibility: { enabled: false },
    chart: { styledMode: true, zooming: { type: 'x' } },
    tooltip: {
      valueDecimals: precision,
      valueSuffix: '%',
    },
    exporting: {
      filename: `${title}-${backTestId}`,
      buttons: {
        contextButton: {
          menuItems: [...exportMenuItems],
        },
      },
      fallbackToExportServer: false,
      chartOptions: {
        chart: { styledMode: false, width: 1000, height: 400, backgroundColor },
        colors: [sgbsTheme === 'dark' ? '#ffffff' : '#000000', '#fe757c'],
        legend: {
          itemStyle: { color: primaryColor },
        },
        xAxis: {
          title: { style: { color: primaryColor } },
          labels: { style: { color: primaryColor } },
          lineColor: primaryColor,
        },
        yAxis: {
          title: {
            style: { color: primaryColor },
          },
          gridLineColor: secondaryColor,
          tickAmount: 5,
          labels: {
            style: { color: primaryColor },
          },
        },
      },
    },
    legend: {
      enabled: true,
      align: 'right',
      className: 'mr-4',
      layout: 'horizontal',
      verticalAlign: 'top',
      itemMarginTop: 30,
      labelFormatter,
    },
    xAxis: {
      title: { text: xAxisTitle, style: { color: primaryColor } },
      labels: { style: { color: primaryColor } },
      lineColor: primaryColor,
    },
    yAxis: {
      title: {
        text: yAxisTitle,
        style: { color: primaryColor },
      },
      lineColor: primaryColor,
      labels: {
        format: PERCENT_FORMAT,
        style: { color: primaryColor },
      },
    },
    title: { text: title },
    series: optionSeries,
  };
}
